// src/contexts/SenhaObrigatoriaContext.tsx
// Gate de TROCA OBRIGATÓRIA de senha (issue #27). Espelha o singleton bridge de
// TermsContext (ADR-004): o interceptor do axios (fora da árvore React) chama
// `requestSenhaChange` para abrir o modal e aguardar a troca, sem re-login. No
// sucesso, o interceptor repete a request originalmente bloqueada.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTheme } from './ThemeContext';
import { useAuth } from './AuthContext';
import { apiPut } from '@/services/api';
import { Toast } from './ToastContext';
import SenhaObrigatoriaModal from '@/components/common/SenhaObrigatoriaModal';
import type { SenhaMotivo } from '@/types/senha';
import { logger } from '@/utils/logger';

interface SenhaObrigatoriaContextValue {
  // Abre o modal de troca obrigatória e resolve true (trocou) / false (saiu).
  openChange: (motivo: SenhaMotivo, padraoExigido: string) => Promise<boolean>;
}

const SenhaObrigatoriaContext = createContext<SenhaObrigatoriaContextValue | undefined>(undefined);

// Singleton bridge para callers fora do React (interceptor em api.ts). Mesmo
// padrão de `termsApi` (TermsContext).
let senhaApi: SenhaObrigatoriaContextValue | null = null;

// Single-flight: vários 403 `senha_pendente` simultâneos compartilham UMA promise
// → um único modal; todas retomam após a troca.
let inflightSenhaChange: Promise<boolean> | null = null;

export const requestSenhaChange = (
  motivo: SenhaMotivo,
  padraoExigido: string
): Promise<boolean> => {
  if (inflightSenhaChange) return inflightSenhaChange;
  if (!senhaApi) {
    logger.warn('requestSenhaChange chamado antes do SenhaObrigatoriaProvider montar; ignorado');
    return Promise.resolve(false);
  }
  inflightSenhaChange = senhaApi.openChange(motivo, padraoExigido).finally(() => {
    inflightSenhaChange = null;
  });
  return inflightSenhaChange;
};

export const SenhaObrigatoriaProvider = ({ children }: { children: ReactNode }) => {
  const { theme } = useTheme();
  const { logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [motivo, setMotivo] = useState<SenhaMotivo>('primeiro_acesso');
  const [padraoExigido, setPadraoExigido] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Promise pendente da troca (resolvida em settle).
  const resolveRef = useRef<((changed: boolean) => void) | null>(null);

  const openChange = useCallback((m: SenhaMotivo, padrao: string): Promise<boolean> => {
    setMotivo(m);
    setPadraoExigido(padrao);
    setErrorMsg(null);
    setSubmitting(false);
    setIsOpen(true);
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const settle = useCallback((changed: boolean) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    setIsOpen(false);
    setSubmitting(false);
    if (resolve) resolve(changed);
  }, []);

  const handleSubmit = useCallback(
    async (senhaAtual: string, novaSenha: string) => {
      setSubmitting(true);
      setErrorMsg(null);
      try {
        // Contrato preexistente: minha-senha responde HTTP 200 com
        // { sucesso, mensagem } mesmo quando a senha é rejeitada pela política.
        const resp = await apiPut('/v1/pessoas/usuarios/minha-senha', { senhaAtual, novaSenha });
        const data = resp.data as { sucesso?: boolean; mensagem?: string };
        if (data.sucesso) {
          // Backend dispara e-mail de confirmação automaticamente (issue #27 item 7).
          Toast.success(data.mensagem || 'Senha alterada. Enviamos um e-mail de confirmação.');
          settle(true); // interceptor repete a request originalmente bloqueada
        } else {
          setErrorMsg(data.mensagem || 'Não foi possível trocar a senha.');
          setSubmitting(false);
        }
      } catch (err) {
        // minha-senha não usa 4xx para erro de política — um catch aqui é 401/500/rede.
        const apiErr = err as { response?: { data?: { mensagem?: string; error?: string } } };
        setErrorMsg(
          apiErr.response?.data?.mensagem ??
            apiErr.response?.data?.error ??
            'Erro ao trocar a senha. Tente novamente.'
        );
        setSubmitting(false);
      }
    },
    [settle]
  );

  const handleLogout = useCallback(() => {
    // "Sair" do gate obrigatório: única saída honesta se o usuário não puder/quiser
    // trocar agora. Resolve false (interceptor repropaga o 403) e faz logout.
    settle(false);
    logout();
  }, [settle, logout]);

  const value = useMemo<SenhaObrigatoriaContextValue>(() => ({ openChange }), [openChange]);

  useEffect(() => {
    senhaApi = value;
    return () => {
      if (senhaApi === value) senhaApi = null;
    };
  }, [value]);

  return (
    <SenhaObrigatoriaContext.Provider value={value}>
      {children}
      <SenhaObrigatoriaModal
        isOpen={isOpen}
        theme={theme}
        motivo={motivo}
        padraoExigido={padraoExigido}
        submitting={submitting}
        errorMsg={errorMsg}
        onSubmit={handleSubmit}
        onLogout={handleLogout}
      />
    </SenhaObrigatoriaContext.Provider>
  );
};

export const useSenhaObrigatoria = (): SenhaObrigatoriaContextValue => {
  const ctx = useContext(SenhaObrigatoriaContext);
  if (!ctx) {
    throw new Error('useSenhaObrigatoria deve ser usado dentro de um SenhaObrigatoriaProvider');
  }
  return ctx;
};
